// src/components/ShippingReportModal.js
import React, { useEffect, useState } from 'react';
import '../pages/AdminOrders.css';
import { adminFetch } from '../api/admin';
import { useToast } from './ToastProvider';
import { BarChart, Truck, PackageCheck } from 'lucide-react';

function formatCurrency(amount) {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
    }).format(amount);
}

export default function ShippingReportModal({ onClose }) {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const { showToast } = useToast();

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const res = await adminFetch('shipping-stats.php');
                const data = await res.json();

                if (data.ok) {
                    setStats(data.stats);
                } else {
                    throw new Error(data.error || 'Failed to load stats');
                }
            } catch (err) {
                console.error("Shipping stats error:", err);
                showToast('Failed to load shipping report', 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, [showToast]);

    return (
        <>
            <div className="admin-modal-backdrop" onClick={onClose} />
            <div className="admin-modal" role="dialog" aria-modal="true">
                <div className="admin-modal-card" style={{ maxWidth: '800px', width: '90%' }}>
                    <button className="admin-modal-close" onClick={onClose}>×</button>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                        <BarChart size={24} color="#d14c87" />
                        <h3 className="admin-modal-title" style={{ margin: 0 }}>Shipping Revenue Report</h3>
                    </div>

                    {loading ? (
                        <div className="admin-loading" style={{ padding: '40px' }}>Loading report...</div>
                    ) : !stats ? (
                        <div className="admin-empty">No data available</div>
                    ) : (
                        <div className="admin-report-content">
                            {/* Summary Cards */}
                            <div className="admin-stats" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: '30px' }}>
                                <div className="admin-stat-card">
                                    <div className="admin-stat-label">Total Shipping Revenue</div>
                                    <div className="admin-stat-value" style={{ color: '#d14c87' }}>
                                        {formatCurrency(stats.total_shipping_revenue)}
                                    </div>
                                </div>
                                <div className="admin-stat-card">
                                    <div className="admin-stat-label">Free Shipping Orders</div>
                                    <div className="admin-stat-value" style={{ color: '#4CAF50' }}>
                                        {stats.free_shipping_count}
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '5px' }}>
                                        Avg. Order: {formatCurrency(stats.avg_order_value_free)}
                                    </div>
                                </div>
                                <div className="admin-stat-card">
                                    <div className="admin-stat-label">Paid Shipping Orders</div>
                                    <div className="admin-stat-value">
                                        {stats.paid_shipping_count}
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '5px' }}>
                                        Avg. Order: {formatCurrency(stats.avg_order_value_paid)}
                                    </div>
                                </div>
                            </div>

                            <h4 style={{ fontSize: '1.1rem', marginBottom: '15px', color: '#444' }}>Location & Rate Breakdown</h4>

                            <div className="admin-block" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                                <div style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                        <span style={{ fontWeight: 600, color: '#444' }}>Ahmedabad (₹80)</span>
                                        <Truck size={18} color="#666" />
                                    </div>
                                    <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#333' }}>
                                        {stats.ahmedabad_count}
                                    </div>
                                    <div style={{ fontSize: '0.9rem', color: '#666' }}>orders</div>
                                </div>

                                <div style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                        <span style={{ fontWeight: 600, color: '#444' }}>Other Gujarat (₹100)</span>
                                        <Truck size={18} color="#666" />
                                    </div>
                                    <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#333' }}>
                                        {stats.other_gujarat_count}
                                    </div>
                                    <div style={{ fontSize: '0.9rem', color: '#666' }}>orders</div>
                                </div>

                                <div style={{ padding: '15px', background: 'rgba(76, 175, 80, 0.1)', borderRadius: '8px', border: '1px dashed #4CAF50' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                        <span style={{ fontWeight: 600, color: '#2E7D32' }}>Free Shipping (₹0)</span>
                                        <PackageCheck size={18} color="#2E7D32" />
                                    </div>
                                    <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#2E7D32' }}>
                                        {stats.free_shipping_count}
                                    </div>
                                    <div style={{ fontSize: '0.9rem', color: '#4CAF50', fontWeight: 600 }}>
                                        {((stats.free_shipping_count / (stats.total_orders || 1)) * 100).toFixed(1)}% of total orders
                                    </div>
                                </div>
                            </div>

                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
